/**
 * Municipality configuration for applicant approval emails.
 * Pure helpers with no external dependencies.
 */
import {
  computeLicenseStatusType,
  LicenseStatusType
} from './license.js';

export const LicenseApprovalEmailMode = Object.freeze({
  disabled: 'disabled',
  confirmBeforeSending: 'confirm_before_sending',
  sendAutomatically: 'send_automatically'
});

export const LICENSE_APPROVAL_EMAIL_MODES = Object.freeze(
  Object.values(LicenseApprovalEmailMode)
);

export const LICENSE_APPROVAL_EMAIL_CUSTOM_MESSAGE_MAX_LENGTH = 5000;

export const LicenseApprovalNotificationDecision = Object.freeze({
  send: 'send',
  skip: 'skip'
});

export const LicenseApprovalNotificationOutcome = Object.freeze({
  queued: 'queued',
  disabled: 'disabled',
  skippedByStaff: 'skipped_by_staff',
  missingRecipient: 'missing_recipient',
  alreadyQueued: 'already_queued'
});

export function isLicenseApprovalEmailMode(value) {
  return LICENSE_APPROVAL_EMAIL_MODES.includes(value);
}

export function normalizeLicenseApprovalEmailCustomMessage(value) {
  if (typeof value !== 'string') return null;
  return value.trim() || null;
}

export function normalizeLicenseApprovalEmailTriggerStatusCode(value) {
  if (typeof value !== 'string') return null;
  return value.trim() || null;
}

export function buildDefaultLicenseApprovalEmailMessage(address) {
  const locationText = address ? ` for ${address}` : '';
  return `Your license application${locationText} has been approved. Your license is now active.`;
}

export function resolveLicenseApprovalEmailMessage({ customMessage, address } = {}) {
  return normalizeLicenseApprovalEmailCustomMessage(customMessage)
    || buildDefaultLicenseApprovalEmailMessage(address);
}

export function normalizeLicenseApprovalEmail(value) {
  const settings = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};

  const normalized = {
    ...settings,
    mode: isLicenseApprovalEmailMode(settings.mode)
      ? settings.mode
      : LicenseApprovalEmailMode.disabled
  };

  if (Object.prototype.hasOwnProperty.call(settings, 'customMessage')) {
    normalized.customMessage = normalizeLicenseApprovalEmailCustomMessage(settings.customMessage);
  }
  if (Object.prototype.hasOwnProperty.call(settings, 'triggerStatusCode')) {
    normalized.triggerStatusCode = normalizeLicenseApprovalEmailTriggerStatusCode(
      settings.triggerStatusCode
    );
  }

  return normalized;
}

export function isLicenseApprovalNotificationDecision(value) {
  return Object.values(LicenseApprovalNotificationDecision).includes(value);
}

/**
 * Approval notifications are eligible when an existing license enters the
 * municipality's configured trigger status. Municipalities without an
 * explicit trigger retain the original first-active-status behavior.
 */
export function isLicenseApprovalTransition({
  previousStatus,
  nextStatus,
  statusConfig = [],
  triggerStatusCode
} = {}) {
  if (!nextStatus || previousStatus === nextStatus) {
    return false;
  }

  const normalizedTriggerStatusCode = normalizeLicenseApprovalEmailTriggerStatusCode(
    triggerStatusCode
  );
  if (normalizedTriggerStatusCode) {
    return nextStatus === normalizedTriggerStatusCode;
  }

  const previousType = computeLicenseStatusType({
    workflowStatus: previousStatus,
    isExpired: false,
    statusConfig
  });
  const nextType = computeLicenseStatusType({
    workflowStatus: nextStatus,
    isExpired: false,
    statusConfig
  });

  return previousType !== LicenseStatusType.active
    && nextType === LicenseStatusType.active;
}

/**
 * On-demand approval emails are available only while the license is in the
 * same destination status that would make an automatic transition eligible.
 */
export function isLicenseApprovalEmailStatusEligible({
  currentStatus,
  statusConfig = [],
  triggerStatusCode
} = {}) {
  if (!currentStatus) {
    return false;
  }

  const normalizedTriggerStatusCode = normalizeLicenseApprovalEmailTriggerStatusCode(
    triggerStatusCode
  );
  if (normalizedTriggerStatusCode) {
    return currentStatus === normalizedTriggerStatusCode;
  }

  return computeLicenseStatusType({
    workflowStatus: currentStatus,
    isExpired: false,
    statusConfig
  }) === LicenseStatusType.active;
}

export function shouldConfirmLicenseApprovalNotification({
  mode,
  previousStatus,
  nextStatus,
  statusConfig = [],
  triggerStatusCode
} = {}) {
  return mode === LicenseApprovalEmailMode.confirmBeforeSending
    && isLicenseApprovalTransition({
      previousStatus,
      nextStatus,
      statusConfig,
      triggerStatusCode
    });
}
